import React, { useState, useRef } from 'react';
import { View, Text, StyleSheet, Dimensions, TouchableOpacity, FlatList } from 'react-native';
import { MaterialIcons, MaterialCommunityIcons, FontAwesome5 } from '@expo/vector-icons';
import { useNavigation } from '@react-navigation/native';
import AsyncStorage from '@react-native-async-storage/async-storage';
import { colors } from '../constants/colors';

const { width } = Dimensions.get('window');

const features = [
  {
    title: 'Welcome to Student Life!',
    description: 'Your all-in-one companion for campus life',
    icon: (color: string) => <MaterialIcons name="school" size={60} color={color} />
  },
  {
    title: 'Timetable Management',
    description: 'Easily manage your class schedule. Import, view, and organize your timetable all in one place.',
    icon: (color: string) => <MaterialIcons name="schedule" size={60} color={color} />
  },
  {
    title: 'Student Announcements',
    description: 'Stay updated with important campus announcements. React and engage with your community.',
    icon: (color: string) => <MaterialIcons name="announcement" size={60} color={color} />
  },
  {
    title: 'Campus Map',
    description: 'Navigate your way around campus with our interactive map feature.',
    icon: (color: string) => <MaterialCommunityIcons name="map-marker-radius" size={60} color={color} />
  },
  {
    title: 'Campus Resources',
    description: 'Access important student resources, documents, and information all in one place.',
    icon: (color: string) => <FontAwesome5 name="book-reader" size={55} color={color} />
  }
];

interface HowToScreenProps {
  route: {
    params: {
      setHasSeenHowTo: (value: boolean) => void;
    };
  };
}

export default function HowToScreen({ route }: HowToScreenProps) {
  const navigation = useNavigation();
  const [currentIndex, setCurrentIndex] = useState(0);
  const flatListRef = useRef<FlatList>(null);
  const { setHasSeenHowTo } = route.params;

  const handleComplete = async () => {
    try {
      await AsyncStorage.setItem('hasSeenHowTo', 'true');
      setHasSeenHowTo(true);
    } catch (error) {
      console.error('Error saving How2Screen state:', error);
    }
  };

  const handleNext = () => {
    if (currentIndex === features.length - 1) {
      handleComplete();
    } else {
      const nextIndex = currentIndex + 1;
      setCurrentIndex(nextIndex);
      flatListRef.current?.scrollToIndex({ index: nextIndex, animated: true });
    }
  };

  const renderSlide = ({ item, index }: { item: typeof features[0], index: number }) => (
    <View style={styles.slide}>
      <View style={styles.iconContainer}>
        {item.icon(colors.primary)}
      </View>
      <Text style={styles.title}>{item.title}</Text>
      <Text style={styles.description}>{item.description}</Text>
    </View>
  );

  const renderDots = () => (
    <View style={styles.dotsContainer}>
      {features.map((_, index) => (
        <View
          key={index}
          style={[
            styles.dot,
            index === currentIndex && styles.activeDot
          ]}
        />
      ))}
    </View>
  );

  return (
    <View style={styles.container}>
      <FlatList
        ref={flatListRef}
        data={features}
        renderItem={renderSlide}
        horizontal
        pagingEnabled
        showsHorizontalScrollIndicator={false}
        onMomentumScrollEnd={(event) => {
          const newIndex = Math.round(event.nativeEvent.contentOffset.x / width);
          setCurrentIndex(newIndex);
        }}
        keyExtractor={(_, index) => index.toString()}
      />

      {renderDots()}

      <View style={styles.buttonContainer}>
        <TouchableOpacity 
          style={styles.skipButton} 
          onPress={handleComplete}
        >
          <Text style={styles.skipText}>Skip</Text>
        </TouchableOpacity>

        <TouchableOpacity 
          style={styles.nextButton} 
          onPress={handleNext}
        >
          <Text style={styles.nextText}>
            {currentIndex === features.length - 1 ? 'Get Started' : 'Next'}
          </Text>
        </TouchableOpacity>
      </View>
    </View>
  );
}

const styles = StyleSheet.create({
  container: {
    flex: 1,
    backgroundColor: colors.background,
  },
  slide: {
    width: width,
    alignItems: 'center',
    justifyContent: 'center',
    padding: 40,
  },
  iconContainer: {
    width: 120,
    height: 120,
    borderRadius: 60,
    backgroundColor: colors.surfaceVariant,
    justifyContent: 'center',
    alignItems: 'center',
    marginBottom: 40,
  },
  title: {
    fontSize: 24,
    fontWeight: 'bold',
    color: colors.text,
    textAlign: 'center',
    marginBottom: 16,
  },
  description: {
    fontSize: 16,
    color: colors.textSecondary,
    textAlign: 'center',
    lineHeight: 24,
  },
  dotsContainer: {
    flexDirection: 'row',
    justifyContent: 'center',
    alignItems: 'center',
    marginBottom: 40,
  },
  dot: {
    width: 8,
    height: 8,
    borderRadius: 4,
    backgroundColor: colors.surfaceVariant,
    marginHorizontal: 4,
  },
  activeDot: {
    backgroundColor: colors.primary,
    width: 16,
  },
  buttonContainer: {
    flexDirection: 'row',
    justifyContent: 'space-between',
    alignItems: 'center',
    paddingHorizontal: 20,
    paddingBottom: 40,
  },
  skipButton: {
    padding: 16,
  },
  skipText: {
    color: colors.textSecondary,
    fontSize: 16,
    fontWeight: '600',
  },
  nextButton: {
    backgroundColor: colors.primary,
    paddingVertical: 16,
    paddingHorizontal: 32,
    borderRadius: 12,
  },
  nextText: {
    color: colors.onPrimary,
    fontSize: 16,
    fontWeight: '600',
  },
});
